import { useIsMobile } from '@newfront-insurance/admin-ui';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useMemo, type ReactNode } from 'react';

import { ChatContainer } from '../components/chat-container';
import type { ChatContentProps } from '../components/chat-content';
import {
  AccountSelector,
  ChatHeader,
  ChatHeaderActions,
  ChatMinimizeButton,
  GeneralFeedbackButton,
  NewChatButton,
  ToggleSidebar,
} from '../components/chat-header';
import { ChatRoot } from '../components/chat-root';
import {
  ChatSidebar,
  ChatSidebarFilterTabs,
  ChatSidebarHistory,
  ChatSidebarNewChat,
  ChatSidebarSearch,
  ChatSidebarScheduledButton,
  ChatSidebarSettingsButton,
} from '../components/chat-sidebar';
import { NovaScreen } from '../components/nova-screen';
import { SettingsPanelProvider } from '../components/settings-panel-provider';
import { ArtifactPreviewProvider } from '../components/tools/artifact-preview/artifact-preview-provider';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { NovaChatContentOptionsContext } from '../contexts/nova-chat-content-options-context';
import { SkillsProvider } from '../contexts/skills-context';
import { createDefaultDataPartRenderers, createDefaultToolRenderers } from '../lib/default-renderers';
import { normalizeChatUuid } from '../lib/notification-helpers';
import { useNovaNavigation } from '../navigation/nova-navigation-context';
import { NovaChatProvider } from '../providers';

/**
 * Container component that renders the chat UI.
 * Reads chat state from context, with an optional app-specific header accessory.
 * All child components are self-contained and manage their own state.
 */
export function NovaChatContainer({ headerAccessory }: { headerAccessory?: ReactNode } = {}) {
  return (
    <NovaChatShell headerAccessory={headerAccessory}>
      <MemoryNovaScreen />
    </NovaChatShell>
  );
}
function MemoryNovaScreen() {
  const { view } = useNovaNavigation();
  return <NovaScreen view={view} />;
}
export function NovaChatShell({
  headerAccessory,
  children,
  toolRenderers: customToolRenderers,
  browserUseEnabled = true,
}: {
  headerAccessory?: ReactNode;
  children: ReactNode;
  toolRenderers?: ChatContentProps['toolRenderers'];
  browserUseEnabled?: boolean;
}) {
  const {
    isVisible,
    authProvider,
    userUuid,
    accountUuid,
    userScopes: scopes,
    chatUuid,
    initialMessages,
    isLoadingMessages: loading,
    placeholder,
    isStandaloneMode,
    isEmbedded,
    isSidebarOpen,
    isFullscreen,
    isSettingsOpen,
  } = useChatOverlayContext();
  const { isMobile } = useIsMobile();
  const desktopDockedSidebarClasses =
    !isMobile && !isFullscreen && !isEmbedded ? 'rounded-tr-sm rounded-tl-none' : undefined;
  const chatFrameClassName = cn('flex h-full min-w-0 flex-1 flex-col transition-[margin-left] duration-200', {
    'ml-[288px]': (isStandaloneMode || isFullscreen || isEmbedded) && isSidebarOpen && !isMobile,
    'ml-[63px]': (isStandaloneMode || isFullscreen || isEmbedded) && !isSidebarOpen && !isMobile,
  });

  // Create default renderers (memoized to avoid recreating on every render)
  const toolRenderers = useMemo(() => customToolRenderers ?? createDefaultToolRenderers(), [customToolRenderers]);
  // The Nova navigator ships the `browserUse` tool, so opt into the
  // `browserStep` renderer here. Other apps (accounts, claims, payments…)
  // that mount `ChatOverlayProviderV2` intentionally leave it off — they
  // don't stage the DCV runtime assets and shouldn't bundle the SDK.
  const dataPartRenderers = useMemo(() => createDefaultDataPartRenderers({ browserUseEnabled }), [browserUseEnabled]);

  const contentProps = useMemo(
    () => ({
      isLoading: loading,
      toolRenderers,
      dataPartRenderers,
      placeholder,
      withArtifactPreviewProvider: false,
      withBrowserPreviewProvider: browserUseEnabled,
    }),
    [loading, toolRenderers, dataPartRenderers, placeholder, browserUseEnabled],
  );

  return (
    <NovaChatContentOptionsContext.Provider value={contentProps}>
      <SkillsProvider>
        <SettingsPanelProvider>
          <ChatContainer isVisible={isVisible} className={desktopDockedSidebarClasses}>
            <NovaChatProvider
              authProvider={authProvider}
              userUuid={userUuid}
              accountUuid={accountUuid}
              scopes={scopes}
              initialMessages={initialMessages}
            >
              <ChatSidebar>
                <ChatSidebarNewChat />
                <ChatSidebarScheduledButton />
                <ChatSidebarSettingsButton />
                <ChatSidebarSearch />
                <ChatSidebarFilterTabs />
                <ChatSidebarHistory />
              </ChatSidebar>
              <ChatRoot key={normalizeChatUuid(chatUuid)}>
                <div className={chatFrameClassName}>
                  <ChatHeader className={desktopDockedSidebarClasses}>
                    {isSettingsOpen ? (
                      <>
                        <div className="flex min-w-0 items-center gap-3.5">
                          {isMobile ? <ToggleSidebar /> : null}
                          {headerAccessory}
                          <h2 className="text-muted-foreground text-[17px] leading-6 font-normal">Settings</h2>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <GeneralFeedbackButton />
                          <ChatHeaderActions />
                        </div>
                      </>
                    ) : isMobile ? (
                      <>
                        <div className="flex shrink-0 items-center gap-2">
                          <ToggleSidebar />
                          {headerAccessory}
                        </div>
                        <div className="flex min-w-0 flex-1 justify-center px-2">
                          <AccountSelector className="min-w-0 truncate" />
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <GeneralFeedbackButton />
                          <NewChatButton />
                          <ChatMinimizeButton />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex min-w-0 items-center gap-3.5">
                          {headerAccessory}
                          <AccountSelector />
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <GeneralFeedbackButton />
                          <ChatHeaderActions />
                        </div>
                      </>
                    )}
                  </ChatHeader>
                  <ArtifactPreviewProvider className="min-h-0 min-w-0 flex-1" contentClassName="size-full min-w-0">
                    {children}
                  </ArtifactPreviewProvider>
                </div>
              </ChatRoot>
            </NovaChatProvider>
          </ChatContainer>
        </SettingsPanelProvider>
      </SkillsProvider>
    </NovaChatContentOptionsContext.Provider>
  );
}
